/*
 * <hero-carousel-a11y>: accessibility layer for the homepage hero slideshow
 * (sections/hero-slideshow-with-content.liquid).
 *
 * The slideshow itself is Flickity, set up by theme.Slideshow in theme.js.
 * This element only adds behavior around it, so theme.js and the other
 * sliders that share it stay untouched. It follows the WAI-ARIA APG
 * carousel pattern:
 * - slides Flickity hides (aria-hidden) are made inert, so Tab skips them
 * - the section renders its own controls (a "Show slide N" dot per slide
 *   and the pause button), so Flickity has no arrows or page dots and its
 *   container leaves the Tab order (it had no name and no visible focus);
 *   the current slide's dot carries aria-current="true"
 * - rotation stops for good when keyboard focus reaches a dot or the
 *   slides, when a dot is chosen, or when Flickity stops it (swipe, tap),
 *   and only the rotation button restarts it; hovering the slides or the
 *   controls pauses it
 * - rotation only ever uses Flickity's stop and play: Flickity's "pause"
 *   resumes by itself when the browser tab becomes visible again, and a
 *   play() queued in a hidden tab is cancelled whenever rotation should stop
 * - if a slide is about to hide while it holds focus, focus moves to the
 *   new slide's dot instead of falling back to the page
 * - with prefers-reduced-motion, rotation starts stopped
 * - slide changes made while rotation is stopped are announced
 */
if (!customElements.get('hero-carousel-a11y')) {
  customElements.define(
    'hero-carousel-a11y',
    class HeroCarouselA11y extends HTMLElement {
      connectedCallback() {
        this.hero = this.querySelector('.hero');
        if (!this.hero) return;

        this.wrapper = this.querySelector('.slideshow-wrapper') || this;
        this.pauseBtn = this.querySelector('.slideshow__pause');
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.status = this.querySelector('[data-carousel-status]');
        this.slideCount = this.hero.querySelectorAll('.slideshow__slide').length;
        this.autoplay = Boolean(this.pauseBtn) && this.hero.dataset.autoplay === 'true' && this.slideCount > 1;
        this.flkty = null;
        this.hovering = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onHeroEnter = this.onHeroEnter.bind(this);
        this.onHeroLeave = this.onHeroLeave.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onRotationStopped = this.onRotationStopped.bind(this);
        this.onSlideChange = this.onSlideChange.bind(this);
        this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);

        // Flickity can start after this element connects (deferred scripts,
        // Theme Editor re-renders), so watch for it here.
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.hero, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'tabindex', 'class'],
          subtree: true
        });

        this.wrapper.addEventListener('mouseenter', this.onHeroEnter);
        this.wrapper.addEventListener('mouseleave', this.onHeroLeave);
        this.addEventListener('focusin', this.onFocusIn);
        // Bubbles up after theme.js's own handler on the pause button has run.
        this.addEventListener('click', this.onClick);
        this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.sync();
      }

      disconnectedCallback() {
        if (!this.hero) return;
        this.observer.disconnect();
        this.wrapper.removeEventListener('mouseenter', this.onHeroEnter);
        this.wrapper.removeEventListener('mouseleave', this.onHeroLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('click', this.onClick);
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.flkty) {
          this.flkty.off('uiChange', this.onRotationStopped);
          this.flkty.off('pointerDown', this.onRotationStopped);
          this.flkty.off('change', this.onSlideChange);
          this.flkty = null;
        }
      }

      sync() {
        this.hero.querySelectorAll('.slideshow__slide').forEach((slide) => {
          const hidden = slide.getAttribute('aria-hidden') === 'true';
          if (slide.inert === hidden) return;
          if (hidden && slide.contains(document.activeElement)) {
            const activeDot = this.dots[this.flkty ? this.flkty.selectedIndex : 0];
            (activeDot || this.pauseBtn || this.hero).focus({ preventScroll: true });
          }
          slide.inert = hidden;
        });
        // The dots replace Flickity's keyboard arrows on the container.
        if (this.dots.length && this.hero.getAttribute('tabindex') === '0') {
          this.hero.setAttribute('tabindex', '-1');
        }
        this.attachFlickity();
      }

      attachFlickity() {
        if (this.flkty || !window.Flickity || typeof window.Flickity.data !== 'function') return;
        const flkty = window.Flickity.data(this.hero);
        if (!flkty) return;
        this.flkty = flkty;

        // Flickity stops autoplay on any drag or tap.
        flkty.on('uiChange', this.onRotationStopped);
        flkty.on('pointerDown', this.onRotationStopped);
        flkty.on('change', this.onSlideChange);

        // The Theme Editor can start on a slide other than the first.
        this.updateDots(flkty.selectedIndex);

        if (this.reducedMotion.matches) {
          this.stopRotation();
        } else {
          this.updatePlayer();
          // Flickity is usually already playing here, so start slide 1's
          // progress bar from empty instead of showing it full.
          if (this.getAttribute('data-rotation') === 'on') this.restartProgress();
        }
      }

      isUserStopped() {
        return Boolean(this.pauseBtn && this.pauseBtn.classList.contains('is-paused'));
      }

      // The one place that starts or stops rotation. data-rotation follows
      // intent, since in a hidden tab Flickity queues play() until the tab
      // is shown and its state still reads "stopped" until then.
      updatePlayer() {
        const flkty = this.flkty;
        const shouldPlay = Boolean(flkty && flkty.player && this.autoplay) && !this.isUserStopped() && !this.hovering;
        if (flkty && flkty.player && this.autoplay) {
          if (shouldPlay) {
            if (flkty.player.state !== 'playing') {
              flkty.playPlayer();
              this.restartProgress();
            }
            if (this.status) this.status.textContent = '';
          } else {
            flkty.stopPlayer();
            // Flickity's stop() leaves a play queued from a hidden tab in place.
            document.removeEventListener('visibilitychange', flkty.player.onVisibilityPlay);
          }
        }
        this.setAttribute('data-rotation', shouldPlay ? 'on' : 'stopped');
      }

      stopRotation() {
        if (this.pauseBtn) this.pauseBtn.classList.add('is-paused');
        this.updatePlayer();
      }

      updateDots(index) {
        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      // Restart the progress bar so it matches Flickity's fresh timer.
      restartProgress() {
        const slide = this.hero.querySelector('.slideshow__slide.is-selected');
        if (!slide) return;
        slide.classList.add('hero-progress-reset');
        void slide.offsetWidth;
        slide.classList.remove('hero-progress-reset');
      }

      onRotationStopped() {
        this.stopRotation();
      }

      onClick(evt) {
        const dot = evt.target.closest('[data-dot]');
        if (dot && this.contains(dot)) {
          this.attachFlickity();
          if (!this.flkty) return;
          // Choosing a slide stops rotation; the change is then announced.
          this.stopRotation();
          this.flkty.select(Number(dot.dataset.dot));
          return;
        }
        if (!this.pauseBtn || !this.pauseBtn.contains(evt.target)) return;
        this.attachFlickity();
        const wasPlaying = this.getAttribute('data-rotation') === 'on';
        // Pressing Play is explicit, even with the pointer resting on the slides.
        if (!this.isUserStopped()) this.hovering = false;
        this.updatePlayer();
        if (!wasPlaying && this.getAttribute('data-rotation') === 'on') this.restartProgress();
      }

      onFocusIn(evt) {
        // Any focus on a dot or in the slides counts, including focus moved
        // by assistive technology. Focus on the pause button alone does not.
        // Mouse presses on the slides already stop rotation through
        // Flickity's pointerDown.
        if (!this.autoplay) return;
        if (!this.hero.contains(evt.target) && !this.dots.includes(evt.target)) return;
        this.stopRotation();
      }

      onHeroEnter() {
        this.hovering = true;
        this.updatePlayer();
      }

      onHeroLeave() {
        this.hovering = false;
        this.updatePlayer();
      }

      onSlideChange(index) {
        this.updateDots(index);
        if (!this.status || !this.flkty || this.flkty.player.state === 'playing') return;
        const message = (this.status.dataset.template || '')
          .replace('[index]', String(index + 1))
          .replace('[total]', String(this.slideCount));
        // Clear first so the same message is announced again if repeated.
        this.status.textContent = '';
        requestAnimationFrame(() => {
          if (this.status) this.status.textContent = message;
        });
      }

      onVisibilityChange() {
        // Let Flickity's own visibility handlers run first, then reassert.
        setTimeout(() => this.updatePlayer(), 0);
      }

      onReducedMotionChange() {
        if (this.reducedMotion.matches) this.stopRotation();
      }
    }
  );
}
